// Shared metrics helpers — colors, gradients, flags, backend-ready ids and CSV export.
export const ACCENT = "#9E1B22";
export const INK = "#171717";

export const PLATFORM_COLORS = {
  spotify: "#1DB954",
  "apple music": "#FA243C",
  apple: "#FA243C",
  youtube: "#FF0000",
  "youtube music": "#FF0000",
  "youtube content id": "#FF0000",
  tiktok: "#00C2D1",
  "amazon music": "#00A8E1",
  amazon: "#00A8E1",
  deezer: "#A238FF",
  shazam: "#1D76F2",
  tidal: "#15C3D6",
  soundcloud: "#FF5500",
  pandora: "#224099",
  napster: "#0066B3",
  instagram: "#E4405F",
  facebook: "#1877F2",
  anghami: "#B02BFF",
};

export const platformColor = (name) => PLATFORM_COLORS[(name || "").toLowerCase()] || ACCENT;

// backend-ready stable id (platform_id / dsp_name slug)
export const platformId = (name) =>
  (name || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

export const platformGradient = (name) => {
  const c = platformColor(name);
  return `linear-gradient(90deg, ${c}59 0%, ${c} 100%)`;
};

export const platformGlow = (name) => `0 2px 12px -2px ${platformColor(name)}80`;

const PLATFORM_ABBR = {
  spotify: "S", "apple music": "", apple: "", youtube: "YT", "youtube music": "YT",
  "youtube content id": "CID", tiktok: "TT", "amazon music": "aMz", amazon: "aMz",
  deezer: "dz", shazam: "Sh", tidal: "Ti", soundcloud: "SC", pandora: "Pa",
};
export const platformAbbr = (name) =>
  PLATFORM_ABBR[(name || "").toLowerCase()] ?? (name || "?").charAt(0).toUpperCase();

export const COUNTRY_CODES = {
  "méxico": "MX", mexico: "MX", "estados unidos": "US", "españa": "ES", espana: "ES",
  colombia: "CO", argentina: "AR", chile: "CL", "perú": "PE", peru: "PE", venezuela: "VE",
  ecuador: "EC", brasil: "BR", brazil: "BR", uruguay: "UY", bolivia: "BO", paraguay: "PY",
  "panamá": "PA", panama: "PA", "costa rica": "CR", guatemala: "GT",
  "república dominicana": "DO", honduras: "HN", "el salvador": "SV", nicaragua: "NI",
  cuba: "CU", "puerto rico": "PR", "reino unido": "GB", francia: "FR", alemania: "DE",
};
export const countryCode = (name) => COUNTRY_CODES[(name || "").toLowerCase()] || "";
export const flagEmoji = (name) => {
  const cc = countryCode(name);
  if (!cc) return "🌐";
  return cc.replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
};

export const citySlug = (name) => (name || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

// Build a backend-ready breakdown object (metrics_breakdown_json) for an entity.
// Values are proportionally derived from global aggregates and flagged estimated:true
// so the same shape is drop-in replaceable by real per-entity backend data later.
export const buildBreakdown = ({ kind, id, name, streams = null, pct = null, revenue = null, series = [], topTracks = [] }) => {
  const share = pct != null ? pct / 100 : 0;
  const history = (series || []).map((p) => ({ date: p.date, streams: Math.round((p.streams || 0) * share) }));
  const totalTop = (topTracks || []).reduce((a, t) => a + (t.streams || 0), 0) || 1;
  const top_tracks = (topTracks || []).slice(0, 5).map((t) => ({
    title: t.title,
    isrc: t.isrc || "",
    streams: Math.round((t.streams || 0) * share),
    revenue_usd: revenue != null ? +(((t.streams || 0) / totalTop) * revenue).toFixed(2) : null,
  }));
  return {
    kind, id, name,
    stream_count: streams,
    pct,
    revenue_usd: revenue,
    estimated: true,
    history,
    top_tracks,
  };
};

export const toCSV = (rows, columns) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = columns.map((c) => esc(c.label)).join(",");
  const body = rows
    .map((r) => columns.map((c) => esc(typeof c.value === "function" ? c.value(r) : r[c.value])).join(","))
    .join("\n");
  return `${header}\n${body}`;
};

export const downloadCSV = (filename, csv) => {
  const blob = new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
